/**
 * Remote log pull (release 3.2 telemetry item 4). An IT admin asks a device
 * for its recent log; the agent uploads it on its next check-in (it only ever
 * polls out: nothing listens on the laptop), redacted on the device and again
 * by the controller; the admin downloads it here. Only device management
 * (foundational:connector:write) sees this tab, and every request, upload and
 * download is in the audit log.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, FileText, Loader2, Send } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { problemDetail } from '@/lib/api';
import { downloadEdgeLog, listEdgeLogRequests, requestEdgeLog, type EdgeLogRequest } from '@/lib/edge-telemetry-api';

export function logStateBadge(r: EdgeLogRequest): { label: string; variant: 'success' | 'warning' | 'critical' | 'glass' } {
  switch (r.state) {
    case 'requested':
      return { label: 'waiting for the device', variant: 'warning' };
    case 'uploaded':
      return r.available ? { label: 'ready to download', variant: 'success' } : { label: 'cleared after 7 days', variant: 'glass' };
    case 'failed':
      return { label: 'failed', variant: 'critical' };
    case 'expired':
      return { label: 'the device did not answer in 24 h', variant: 'glass' };
    default:
      return { label: r.state, variant: 'glass' };
  }
}

/** "182 KiB, 2,310 lines, 14 redacted (6 by the controller), older lines left out". */
export function logSizeText(r: EdgeLogRequest): string | null {
  if (r.contentBytes == null) return null;
  const kb = r.contentBytes >= 1024 ? `${Math.round(r.contentBytes / 1024)} KiB` : `${r.contentBytes} bytes`;
  const lines = r.lineCount != null ? `, ${new Intl.NumberFormat('en-IN').format(r.lineCount)} lines` : '';
  const total = (r.agentRedactions ?? 0) + (r.serverRedactions ?? 0);
  const red = `, ${total} redacted${r.serverRedactions ? ` (${r.serverRedactions} by the controller)` : ''}`;
  return `${kb}${lines}${red}${r.truncated ? ', older lines left out' : ''}`;
}

export function EdgeLogsTab({
  podRowId,
  logPull,
  decommissioned,
}: {
  podRowId: string;
  /** The agent declares collect_logs (release 3.2). */
  logPull: boolean;
  decommissioned: boolean;
}) {
  const [rows, setRows] = useState<EdgeLogRequest[] | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await listEdgeLogRequests(podRowId);
      setRows(r.requests);
    } catch (e) {
      setError(problemDetail(e, 'Could not load the log requests.'));
    }
  }, [podRowId]);

  useEffect(() => {
    void load();
  }, [load]);

  const ask = async () => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await requestEdgeLog(podRowId, { reason: reason.trim() });
      setReason('');
      setNote('Requested. The device uploads its log on its next check-in (within a few minutes when it is online).');
      await load();
    } catch (e) {
      setError(problemDetail(e, 'The request was refused.'));
    } finally {
      setBusy(false);
    }
  };

  const download = async (r: EdgeLogRequest) => {
    setError(null);
    try {
      await downloadEdgeLog(podRowId, r.id);
      await load();
    } catch (e) {
      setError(problemDetail(e, 'The log could not be downloaded.'));
    }
  };

  const canAsk = logPull && !decommissioned;
  return (
    <div className="space-y-3" data-testid="edge-logs">
      <div className="text-xs text-muted-foreground inline-flex items-start gap-1.5">
        <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          Ask this device for its recent log. Nothing on the device listens for this: the agent picks the request up on
          its next check-in and uploads the newest part of its log. File contents, matched values, passwords and tokens
          are removed on the device and again here. The log does keep file and folder paths, and with them the
          Windows user name and the names of the files the agent opened, unless full-path reporting is off on the
          device: then paths read as findings show them (the scanned folder and the path below it) and user names
          are removed. The log is kept for 7 days. Every request and download is audited.
        </span>
      </div>
      {!logPull && (
        <p className="text-xs text-muted-foreground" data-testid="edge-logs-unsupported">
          This agent cannot upload its log: agents from release 3.2 can. Update the agent first.
        </p>
      )}
      {canAsk && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="flex-1 min-w-[16rem] rounded-md border border-border bg-background px-2 py-1 text-xs"
            placeholder="Why you need the log (at least 3 characters)"
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Reason for the log request"
          />
          <Button
            size="sm"
            onClick={() => void ask()}
            disabled={busy || reason.trim().length < 3}
           
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
            Request log
          </Button>
        </div>
      )}
      {note && <p className="text-xs text-foreground/90">{note}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
      {rows === null ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="edge-logs-empty">
          No log has been requested from this device.
        </p>
      ) : (
        <ul className="space-y-2 text-xs">
          {rows.map((r) => {
            const b = logStateBadge(r);
            const size = logSizeText(r);
            return (
              <li key={r.id} className="rounded border border-border/60 p-2 space-y-1" data-testid={`edge-log-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    {new Date(r.requestedAt).toLocaleString()} by {r.requestedBy}
                  </span>
                  <Badge variant={b.variant} className="text-[10px]">
                    {b.label}
                  </Badge>
                </div>
                <div className="text-muted-foreground">Reason: {r.reason}</div>
                {size && <div>{size}</div>}
                {r.failure && <div className="text-destructive">{r.failure}</div>}
                {r.sha256 && <div className="font-mono text-[10px] text-muted-foreground break-all">sha256 {r.sha256}</div>}
                {r.available && (
                  <Button size="sm" variant="glass" onClick={() => void download(r)}>
                    <Download className="h-3.5 w-3.5 mr-1" /> Download
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
